import axios from 'axios';
import { ZodError } from 'zod';

/** Single error type the UI ever sees. Port of `mapToApiException` from the Flutter app. */
export class ApiError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

const TEMP_SERVER = 'خطای موقت سرور؛ لطفاً دوباره تلاش کنید';
const STATUS_FALLBACK: Record<number, string> = {
  400: 'درخواست نامعتبر است',
  401: 'برای ادامه وارد حساب کاربری شوید',
  403: 'دسترسی مجاز نیست',
  404: 'سفر یا منبع مورد نظر پیدا نشد',
  409: 'تداخل در وضعیت سفر؛ لطفاً دوباره تلاش کنید',
  422: 'اطلاعات ارسال‌شده قابل پردازش نیست',
  429: 'تعداد درخواست‌ها زیاد است؛ کمی بعد تلاش کنید',
  500: TEMP_SERVER,
  502: TEMP_SERVER,
  503: TEMP_SERVER,
};

/** DRF can answer with a string, list, `{detail}`, `{message}` or `{field: [msgs]}`. */
export function extractServerMessage(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === 'string') {
    const t = data.trim();
    // HTML error pages (Django debug) or very long bodies are useless to users.
    if (!t || t.startsWith('<') || t.length > 280) return null;
    return t.replace(/^detail:\s*/, '');
  }
  if (Array.isArray(data)) {
    return data.length ? data.map(String).join('\n').replace(/detail:\s*/g, '') : null;
  }
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const key of ['detail', 'message', 'error', 'msg']) {
      const v = obj[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (Array.isArray(v) && v.length) return v.map(String).join('\n');
    }
    const fieldErrors: string[] = [];
    for (const v of Object.values(obj)) {
      if (Array.isArray(v)) fieldErrors.push(...v.map(String));
      else if (typeof v === 'string') fieldErrors.push(v);
    }
    if (fieldErrors.length) return fieldErrors.join('\n');
  }
  return null;
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? null;
    if (axios.isCancel(error)) return new ApiError('درخواست لغو شد', status);
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError('زمان پاسخ‌دهی سرور به پایان رسید', status);
    }
    if (!error.response) return new ApiError('اتصال به اینترنت برقرار نیست', null);
    const extracted = extractServerMessage(error.response.data);
    return new ApiError(
      extracted ?? STATUS_FALLBACK[status ?? 0] ?? `خطای سرور (کد ${status})`,
      status,
    );
  }

  if (error instanceof ZodError) {
    return new ApiError('ساختار دادهٔ سرور با اپ همخوان نیست');
  }
  return new ApiError('خطای غیرمنتظره رخ داد');
}
